import {
  createTheme,
  colors,
  PaletteColor,
  PaletteColorOptions,
} from "@mui/material";

const LAYOUT = {
  topBarHeight: "50px",
};

declare module "@mui/material/styles" {
  interface Theme {
    layout: typeof LAYOUT;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    layout: typeof LAYOUT;
  }
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    narrow: true;
    wide: true;
  }
  interface Palette {
    accessory: PaletteColor;
    accessoryBlue: PaletteColor;
    tertiary: PaletteColor;
    tertiaryBlue: PaletteColor;
    tertiary2: PaletteColor;
  }
  interface PaletteOptions {
    accessory: PaletteColorOptions;
    accessoryBlue: PaletteColorOptions;
    tertiary: PaletteColorOptions;
    tertiaryBlue: PaletteColorOptions;
    tertiary2: PaletteColorOptions;
  }
  interface TypographyVariants {
    h0: React.CSSProperties;
    h0Bold: React.CSSProperties;
    h1Bold: React.CSSProperties;
    h2Bold: React.CSSProperties;
    h2Caps: React.CSSProperties;
    h3Bold: React.CSSProperties;
    h3Caps: React.CSSProperties;
    h4Caps: React.CSSProperties;
    body1: React.CSSProperties;
    body1Bold: React.CSSProperties;
    body2: React.CSSProperties;
    body2Bold: React.CSSProperties;
    body3: React.CSSProperties;
    body3Bold: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    h0?: React.CSSProperties;
    h0Bold?: React.CSSProperties;
    h1Bold?: React.CSSProperties;
    h2Bold?: React.CSSProperties;
    h2Caps?: React.CSSProperties;
    h3Bold?: React.CSSProperties;
    h3Caps?: React.CSSProperties;
    h4Caps?: React.CSSProperties;
    body1?: React.CSSProperties;
    body1Bold?: React.CSSProperties;
    body2?: React.CSSProperties;
    body2Bold?: React.CSSProperties;
    body3?: React.CSSProperties;
    body3Bold?: React.CSSProperties;
  }
}

export const palette = {
  darkGreen: "#006B66",
  midGreen: "#D5F2E4",
  lightGreen: "#E9F8F2",
  lightestGreen: "#F4FBF8",

  purple: "#5A52A7",
  navy: "#222A5C",
  darkestBlue: "#181D40",
  darkBlue: "#0E56AB",
  blue: "#0074DF",
  highlightBlue: "#ADDBE8",
  midBlue: "#BDEFF8",
  lightBlue: "#EBF9FB",
  lightestBlue: "#F3FCFD",

  darkGray: "#6A6783",
  midGray: "#D7D5E0",
  lightGray: "#F0EEF5",
  lightestGray: "#F9F8FB",
  white: "#FFFFFF",

  red: "#C52737",
  orange: "#8C3500",
  midOrange: "#FFEDCE",
  lightOrange: "#FFF4E2",
  lightestOrange: "#FFF8EE",

  // deprecated names, use the ones above instead
  blue4: "#BDEFF8",
  blueHighlight: "#DDF4F8",
};

declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
    tertiaryBlue: true;
    accessory: true;
    accessoryBlue: true;
    tertiary2: true;
    containedTertiary: true;
    containedTertiary2: true;
  }

  interface ButtonPropsVariantOverrides {
    link: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h0: true;
    h0Bold: true;
    h1Bold: true;
    h2Bold: true;
    h2Caps: true;
    h3Bold: true;
    h3Caps: true;
    h4Caps: true;
    body1: true;
    body1Bold: true;
    body2: true;
    body2Bold: true;
    body3: true;
    body3Bold: true;
  }
}

const theme = createTheme({
  palette: {
    background: {
      default: "#FFFFFF",
      paper: colors.common.white,
    },
    primary: {
      main: palette.blue,
      contrastText: colors.common.white,
    },
    secondary: {
      main: palette.navy,
      contrastText: colors.common.white,
    },
    tertiary: {
      main: palette.lightGray,
      contrastText: palette.navy,
    },
    tertiaryBlue: {
      main: palette.blue4,
      contrastText: palette.navy,
    },
    tertiary2: {
      main: palette.midBlue,
      contrastText: palette.navy,
    },
    accessory: {
      main: palette.lightGray,
      contrastText: palette.navy,
    },
    accessoryBlue: {
      main: palette.blue4,
      contrastText: palette.navy,
    },
    text: {
      primary: palette.navy,
      secondary: palette.darkGray,
    },
    error: {
      main: palette.red,
    },
    warning: {
      main: palette.orange,
    },
    ...palette,
  },
  typography: {
    fontFamily: "Poppins", // TODO: fallback fonts
    h0: {
      fontSize: "30px",
      fontWeight: 400,
    },
    h0Bold: {
      fontSize: "26px",
      fontWeight: 700,
      lineHeight: "140%",
    },
    h1: {
      fontSize: "20px",
      fontWeight: 400,
    },
    h1Bold: {
      fontSize: "20px",
      fontWeight: 700,
    },
    h2: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "150%",
    },
    h2Bold: {
      fontSize: "16px",
      fontWeight: 700,
    },
    h2Caps: {
      fontSize: "16px",
      textTransform: "uppercase",
      fontWeight: 500,
    },
    h3Caps: {
      fontSize: "12px",
      textTransform: "uppercase",
      fontWeight: 600,
    },
    h3Bold: {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "140%",
    },
    h4Caps: {
      fontSize: "10px",
      textTransform: "uppercase",
      fontWeight: 500,
    },
    body1: {
      fontSize: "14px",
    },
    body1Bold: {
      fontSize: "14px",
      fontWeight: 700,
    },
    body2: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "140%",
    },
    body2Bold: {
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "140%",
    },
    body3: {
      fontSize: "10px",
      fontWeight: 400,
    },
    body3Bold: {
      fontSize: "10px",
      fontWeight: 600,
    },
  },
  layout: LAYOUT,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      narrow: 0,
      wide: 900,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          height: LAYOUT.topBarHeight,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          border: "1px solid",
          borderColor: palette.midGray,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          backgroundColor: palette.midGray,
          padding: "4px 16px",
        },
        title: {
          fontSize: 12,
          fontWeight: 700,
        },
        action: {
          "& .MuiIconButton-root": {
            color: palette.navy,
          },

          "& .MuiSvgIcon-root": {
            fontSize: "0.7em",
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          switch (ownerState.variant) {
            case "outlined":
              return {
                marginBottom: "16px",
                fontSize: "14px",
                paddingTop: "24px",

                ".MuiTextField-root": {
                  marginBottom: ownerState.error ? "2px" : "25px",
                },

                ".MuiInputBase-root": {
                  minHeight: "40px",

                  borderStyle: "solid",
                  border: "1px solid",
                  borderRadius: 0,
                  position: "relative",
                  backgroundColor: theme.palette.common.white,
                  transition: theme.transitions.create([
                    "border-color",
                    "box-shadow",
                  ]),

                  "&.Mui-disabled": {
                    color: palette.darkGray,
                  },

                  ...(ownerState.error
                    ? {
                        borderColor: palette.red,
                        borderWidth: "2px",
                        ".MuiInputBase-input": {
                          padding: "9px 11px",
                        },
                      }
                    : {
                        borderColor: palette.midGray,
                        borderWidth: "1px",
                        ".MuiInputBase-input": {
                          padding: "10px 12px",
                        },
                        "&:focus, &:focus-within": {
                          borderColor: theme.palette.primary.main,
                          borderWidth: "2px",
                          ".MuiInputBase-input": {
                            padding: "9px 11px",
                          },
                        },
                      }),
                },

                ".MuiFormLabel-root": {
                  color: palette.darkGray,
                  marginLeft: "-12px",
                  marginTop: "12px",

                  "&.Mui-focused": {
                    color: palette.darkGray,
                  },
                },

                ".MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },

                ".MuiOutlinedInput-root": {
                  padding: 0,
                },
              };
            case "standard":
              return {
                ".MuiFormLabel-root": {
                  marginTop: "8px",
                },
              };
            default:
              return {};
          }
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          // TODO: move to formcontrol?
          "&::placeholder": {
            color: palette.darkGray,
            opacity: 1,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&.Mui-disabled": {
            color: palette.darkGray,
            textFillColor: palette.darkGray,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // We actually want the label text to be 12px, but shrink: true is required to position the label correctly
          // (see https://mui.com/material-ui/react-text-field/#shrink)
          // so we're compensating for the .75 scale that shrinking applies
          fontSize: "16px",
          fontWeight: 600,
          "&.Mui-disabled": {
            color: palette.darkGray,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "inherit",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.navy,
          textDecorationColor: palette.navy,
          "&:hover": {
            textDecoration: "underline",
            color: "#181D40",
            textDecorationColor: "#181D40",
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: palette.navy,
          fontWeight: 600,
          "&.Mui-active": {
            fontWeight: 600,
          },
        },
        root: {
          padding: 0,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        lineVertical: {
          borderColor: palette.lightGray,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          strokeWidth: "2px",
          stroke: palette.navy,
          fill: colors.common.white,

          circle: {
            r: "10px",
          },

          ".MuiStepIcon-text": {
            fill: palette.navy,
          },

          "&.Mui-active": {
            fill: palette.navy,

            ".MuiStepIcon-text": {
              fill: colors.common.white,
            },
          },
        },
        text: {
          fontWeight: 600,
          strokeWidth: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 999,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "#F0EEF5",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: palette.white,

          // this bit is for the grey outline around the checkbox.
          // unfortunately, we can't use a simple border here due to the spacing between the input,
          // svg, and path elements (it ends up with a gap between the icon and the border).
          // so instead, we can add an svg path outline to get the same effect:
          "& .MuiSvgIcon-root > path": {
            outline: `1px solid ${palette.midGray}`,
          },
          "&.Mui-checked": {
            color: palette.darkGreen,
          },
        },
        colorSecondary: {
          color: palette.navy,
          "&.Mui-checked": {
            color: palette.navy,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorSecondary: {
          color: palette.navy,
          "&.Mui-checked": {
            color: palette.navy,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: ({ ownerState, theme }) => {
          switch (ownerState.color) {
            case "primary":
              return {
                fontSize: "14px",
                letterSpacing: "0.48px",
                fontWeight: 600,
                height: "40px",
                background: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  background: palette.darkBlue,
                },
              };
            case "secondary":
              return {
                fontSize: "14px",
                fontWeight: 600,
                height: "40px",
                letterSpacing: "0.48px",
                background: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                "&:hover": {
                  background: palette.darkestBlue,
                },
              };
            case "tertiary":
              return {
                fontSize: "14px",
                fontWeight: 600,
                height: "40px",
                letterSpacing: "0.48px",
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
            case "tertiaryBlue":
              return {
                fontSize: "14px",
                fontWeight: 600,
                height: "40px",
                letterSpacing: "0.48px",
                background: theme.palette.tertiaryBlue.main,
                color: theme.palette.tertiaryBlue.contrastText,

                "&:hover": {
                  background: palette.highlightBlue,
                },
              };
            case "accessory":
              return {
                fontSize: "11px",
                fontWeight: 500,
                height: "27px",
                letterSpacing: "0.4px",
                background: theme.palette.accessory.main,
                color: theme.palette.accessory.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
            case "accessoryBlue":
              return {
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.4px",
                background: theme.palette.accessoryBlue.main,
                color: theme.palette.accessoryBlue.contrastText,
                "&:hover": {
                  background: palette.highlightBlue,
                },
              };
            case "tertiary2":
              return {
                background: theme.palette.tertiary2.main,
                color: theme.palette.tertiary2.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
            case "error":
              return {
                background: theme.palette.error.main,
                color: theme.palette.error.contrastText,
                "&:hover": {
                  background: palette.orange,
                },
              };
            default:
              return {
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
                "&:hover": {
                  background: palette.midGray,
                },
              };
          }
        },
        root: {
          borderRadius: 0,
          boxShadow: "none",
          whiteSpace: "nowrap",
          "&.Mui-disabled": {
            background: palette.midGray,
            color: palette.darkGray,
          },
        },
      },
      variants: [
        {
          props: { variant: "link" },
          style: {
            textTransform: "none",
            textDecoration: "underline",
            background: "none",
            "&:hover": {
              cursor: "pointer",
              textDecoration: "underline",
              background: "none",
            },
          },
        },
      ],
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          backgroundColor: palette.lightGray,
          "&:before": {
            display: "none",
          },
          padding: "10px 20px",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          fontWeight: 600,
          size: "16px",
          margin: "20px 0",
        },
        expandIconWrapper: {
          "& .MuiSvgIcon-root": {
            width: "1.5em",
            height: "1.5em",
          },
        },
        root: {
          fontFamily: "inherit",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          switch (ownerState.color) {
            case "tertiary":
              return {
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
              };
          }
          switch (ownerState.severity) {
            case "warning":
              return { backgroundColor: palette.lightOrange };
          }
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          "& .MuiSwitch-thumb": {
            backgroundColor: palette.darkGray,
          },
          "& .MuiSwitch-track": {
            backgroundColor: palette.midGray,
            opacity: 1,
          },
          "& .MuiButtonBase-root.Mui-checked+.MuiSwitch-track": {
            backgroundColor: palette.midGray,
            opacity: 1,
          },
          "& .Mui-checked > .MuiSwitch-thumb": {
            backgroundColor: palette.navy,
          },
        },
      },
    },
  },
});

export default theme;
